/** Day-wise cancellation slabs relative to package departure. */

const OPEN_MAX = null;

const DEFAULT_REFUND_SLABS = [
  { minDays: 45, maxDays: OPEN_MAX, refundPercent: 90 },
  { minDays: 30, maxDays: 44, refundPercent: 75 },
  { minDays: 15, maxDays: 29, refundPercent: 50 },
  { minDays: 0, maxDays: 14, refundPercent: 0 }
];

function parseDay(v) {
  if (v === '' || v == null) return null;
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function isLegacyThresholdSlab(s) {
  return (
    s &&
    s.daysBefore != null &&
    s.minDays == null &&
    s.maxDays == null &&
    s.fromDays == null &&
    s.toDays == null
  );
}

function thresholdsToRanges(thresholds) {
  const sorted = [...thresholds].sort((a, b) => b.daysBefore - a.daysBefore);
  return sorted.map((row, i) => {
    const next = sorted[i + 1];
    return {
      minDays: row.daysBefore,
      maxDays: next ? next.daysBefore - 1 : OPEN_MAX,
      refundPercent: row.refundPercent
    };
  });
}

function normalizeSingleSlab(s) {
  const refundPercent = Math.min(
    100,
    Math.max(0, Math.round(Number(s?.refundPercent ?? s?.refund_percent) || 0))
  );

  if (isLegacyThresholdSlab(s)) {
    return {
      daysBefore: Math.max(0, Math.floor(Number(s.daysBefore) || 0)),
      refundPercent
    };
  }

  let minDays = parseDay(s?.minDays ?? s?.min_days ?? s?.fromDays ?? s?.from_days);
  let maxDays = parseDay(s?.maxDays ?? s?.max_days ?? s?.toDays ?? s?.to_days);

  if (minDays == null && maxDays != null) minDays = 0;
  if (minDays == null) return null;

  if (maxDays != null && maxDays < minDays) {
    const tmp = minDays;
    minDays = maxDays;
    maxDays = tmp;
  }

  return { minDays, maxDays: maxDays ?? OPEN_MAX, refundPercent };
}

export function normalizeRefundSlabs(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  if (!arr.length) return [...DEFAULT_REFUND_SLABS];

  const parsed = arr.map(normalizeSingleSlab).filter(Boolean);
  if (!parsed.length) return [...DEFAULT_REFUND_SLABS];

  if (parsed.every((s) => s.daysBefore != null)) {
    return sortSlabsForDisplay(thresholdsToRanges(parsed));
  }

  return sortSlabsForDisplay(
    parsed.map((s) => ({
      minDays: s.minDays,
      maxDays: s.maxDays ?? OPEN_MAX,
      refundPercent: s.refundPercent
    }))
  );
}

function sortSlabsForDisplay(slabs) {
  return [...slabs].sort((a, b) => {
    const aMax = a.maxDays ?? Infinity;
    const bMax = b.maxDays ?? Infinity;
    if (bMax !== aMax) return bMax - aMax;
    return b.minDays - a.minDays;
  });
}

function parseDepartureDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysBetweenCalendar(start, end) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfLocalDay(end).getTime() - startOfLocalDay(start).getTime()) / msPerDay);
}

function formatDateWithOrdinal(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  const day = d.getDate();
  const suf =
    day === 1 || day === 21 || day === 31
      ? 'st'
      : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
          ? 'rd'
          : 'th';
  return `${day}${suf} ${d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
}

function formatDepartureHeading(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function rangeLabel(minDays, maxDays) {
  if (maxDays == null) {
    return minDays > 0 ? `${minDays}+ days before departure` : 'Until departure';
  }
  if (minDays === maxDays) {
    return `${minDays} day${minDays === 1 ? '' : 's'} before departure`;
  }
  return `${minDays}–${maxDays} days before departure`;
}

function calendarDayKey(date) {
  const d = startOfLocalDay(date);
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function buildSlabDisplayRows(startDate, sortedSlabs) {
  const fmt = formatDateWithOrdinal;
  const after = (d) => addDays(d, 1);

  return sortedSlabs.map((row) => {
    const { minDays, maxDays, refundPercent } = row;
    const noRefund = refundPercent === 0;
    const closeDate = addDays(startDate, -minDays);
    const farDate = maxDays != null ? addDays(startDate, -maxDays) : null;

    let label;
    if (maxDays == null) {
      label = minDays > 0 ? `Till ${fmt(closeDate)}, 11:59 PM` : 'Until departure';
    } else if (minDays === 0 && maxDays > 0) {
      label = `From ${fmt(after(closeDate))}, 12:00 AM until departure (within ${maxDays} days)`;
    } else if (farDate && calendarDayKey(farDate) !== calendarDayKey(closeDate)) {
      label = `From ${fmt(farDate)}, 12:00 AM to ${fmt(closeDate)}, 11:59 PM`;
    } else {
      label = rangeLabel(minDays, maxDays);
    }

    return {
      label,
      text: noRefund ? 'No Refund' : `Get ${refundPercent}% refund from total trip cost`,
      refundPercent,
      noRefund,
      note:
        noRefund && maxDays != null
          ? `In the last ${maxDays + 1} days before departure, no refund is applicable.`
          : undefined
    };
  });
}

function buildGenericSlabRows(sortedSlabs) {
  return sortedSlabs.map((row) => {
    const { minDays, maxDays, refundPercent } = row;
    const noRefund = refundPercent === 0;
    return {
      label: rangeLabel(minDays, maxDays),
      text: noRefund ? 'No Refund' : `Get ${refundPercent}% refund from total trip cost`,
      refundPercent,
      noRefund,
      note:
        noRefund && maxDays != null
          ? `In the last ${maxDays + 1} days before departure, no refund is applicable.`
          : undefined
    };
  });
}

function findCurrentSlabIndex(sortedSlabs, daysUntilDeparture) {
  if (!Number.isFinite(daysUntilDeparture)) return 0;
  if (daysUntilDeparture < 0) return Math.max(0, sortedSlabs.length - 1);

  for (let i = 0; i < sortedSlabs.length; i++) {
    const { minDays, maxDays } = sortedSlabs[i];
    const max = maxDays == null ? Infinity : maxDays;
    if (daysUntilDeparture >= minDays && daysUntilDeparture <= max) return i;
  }

  const openTop = sortedSlabs.find((s) => s.maxDays == null);
  if (openTop && daysUntilDeparture >= openTop.minDays) {
    return sortedSlabs.indexOf(openTop);
  }

  let bestIdx = sortedSlabs.length - 1;
  let bestMax = -1;
  for (let i = 0; i < sortedSlabs.length; i++) {
    const max = sortedSlabs[i].maxDays ?? Infinity;
    if (max > bestMax) {
      bestMax = max;
      bestIdx = i;
    }
  }
  if (daysUntilDeparture > bestMax) return bestIdx;
  return bestIdx;
}

function buildSummaryMessage(slab, hasTotal, totalAmount, daysUntilDeparture) {
  if (!slab) return '';
  const daysPart =
    Number.isFinite(daysUntilDeparture) && daysUntilDeparture >= 0
      ? ` Departure is in ${daysUntilDeparture} day${daysUntilDeparture === 1 ? '' : 's'}.`
      : '';
  const windowPart = slab.label ? ` Window: ${slab.label}.` : '';
  if (slab.noRefund) {
    return `If cancelled today, no refund on total trip cost.${daysPart}${windowPart} Booking amount is non-refundable.`;
  }
  const pct = slab.refundPercent;
  let amountPart = '';
  if (hasTotal && pct > 0) {
    const est = Math.round((totalAmount * pct) / 100);
    amountPart = ` Estimated refund: ₹${est.toLocaleString('en-IN')} (${pct}% of ₹${Math.round(totalAmount).toLocaleString('en-IN')}).`;
  }
  return `If cancelled today, eligible for ${pct}% refund of total trip cost.${amountPart}${daysPart}${windowPart} Booking amount is non-refundable.`;
}

export function getCancellationTimeline(departureDate, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const totalAmount = Number(options.totalAmount);
  const hasTotal = Number.isFinite(totalAmount) && totalAmount > 0;
  const sortedSlabs = normalizeRefundSlabs(options.refundSlabs);

  const startDate = parseDepartureDate(departureDate);
  if (!startDate) {
    const slabs = buildGenericSlabRows(sortedSlabs);
    const currentSlab = slabs[0];
    return {
      departureHeading: '',
      slabs,
      currentIndex: 0,
      currentSlab,
      daysUntilDeparture: null,
      summaryMessage: buildSummaryMessage(currentSlab, hasTotal, totalAmount, null)
    };
  }

  const slabs = buildSlabDisplayRows(startDate, sortedSlabs);
  const daysUntilDeparture = daysBetweenCalendar(now, startDate);
  const currentIndex = findCurrentSlabIndex(sortedSlabs, daysUntilDeparture);
  const currentSlab = slabs[currentIndex] || slabs[0];

  return {
    departureHeading: formatDepartureHeading(startDate),
    slabs,
    currentIndex,
    currentSlab,
    daysUntilDeparture,
    summaryMessage: buildSummaryMessage(currentSlab, hasTotal, totalAmount, daysUntilDeparture)
  };
}
